import clsx from 'clsx';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';

import { ASSET_MANIFEST } from '../assets/manifest';
import { useSettingsStore } from '../state/settings-store';
import '../styles/global.css';
import styles from './LastWreckScreen.module.css';
import { Icon } from './icons';
import { PauseAndSettings } from './PauseAndSettings';
import { RunResults } from './RunResults';
import { ScannerMapOverlay } from './ScannerMapOverlay';
import { MobileControls, TOOL_META } from './VirtualControls';
import type {
  ContextActionState,
  EventMessage,
  ExtractionState,
  LastWreckScreenProps,
  ObjectiveState,
  ToolId,
  ToolState,
} from './types';

const formatTime = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  return `${Math.floor(safeSeconds / 60)
    .toString()
    .padStart(2, '0')}:${(safeSeconds % 60).toString().padStart(2, '0')}`;
};

export function ObjectiveCard({ objective }: { objective: ObjectiveState }) {
  const [manualCollapsed, setManualCollapsed] = useState(false);
  const [targetExpanded, setTargetExpanded] = useState(false);
  const collapsed = objective.targetFound ? !targetExpanded : manualCollapsed;

  return (
    <section className={styles.objectiveCard} data-collapsed={collapsed}>
      <button
        aria-expanded={!collapsed}
        aria-label={collapsed ? 'Expand contract objective' : 'Collapse contract objective'}
        className={styles.objectiveToggle}
        onClick={() => {
          if (objective.targetFound) setTargetExpanded((value) => !value);
          else setManualCollapsed((value) => !value);
        }}
      >
        <Icon name="target" />
        <span>
          <small>CONTRACT 07-A</small>
          <strong>{objective.title}</strong>
        </span>
        <i aria-hidden="true" />
      </button>
      {!collapsed && (
        <div className={styles.objectiveBody}>
          <p>{objective.instruction}</p>
          <div className={styles.objectiveProgress}>
            <span>
              {objective.complete
                ? 'TARGET SECURED'
                : `PHASE ${String(objective.step)} / ${String(objective.totalSteps)}`}
            </span>
            <i>
              <b
                style={{
                  width: `${String((objective.step / objective.totalSteps) * 100)}%`,
                }}
              />
            </i>
          </div>
        </div>
      )}
    </section>
  );
}

export function InstabilityGauge({
  value,
  elapsedSeconds,
  emergencySeconds,
}: {
  value: number;
  elapsedSeconds: number;
  emergencySeconds?: number | undefined;
}) {
  const clamped = Math.min(100, Math.max(0, value));
  const circumference = 2 * Math.PI * 42;
  const danger = clamped >= 85;

  return (
    <section
      aria-label={`Wreck instability ${String(Math.round(clamped))} percent`}
      className={styles.instability}
      data-danger={danger}
    >
      <svg viewBox="0 0 100 100">
        <circle className={styles.gaugeTrack} cx="50" cy="50" r="42" />
        <circle
          className={styles.gaugeValue}
          cx="50"
          cy="50"
          r="42"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - clamped / 100)}
        />
      </svg>
      <div>
        <span>INSTABILITY</span>
        <strong>{Math.round(clamped)}</strong>
        <small>%</small>
      </div>
      <p>
        <Icon name="timer" /> {formatTime(elapsedSeconds)}
      </p>
      {emergencySeconds !== undefined && (
        <b className={styles.emergencyTime}>{Math.ceil(emergencySeconds)} SEC TO BREAK</b>
      )}
    </section>
  );
}

export function ExtractionHud({ extraction }: { extraction: ExtractionState }) {
  return (
    <section className={styles.extractionHud} data-core={extraction.coreBanked}>
      <div>
        <Icon name="cargo" />
        <span>BANKED SALVAGE</span>
      </div>
      <strong>¤ {extraction.bankedValue.toLocaleString()}</strong>
      <i>
        <b
          style={{
            width: `${String(
              Math.min(100, (extraction.bankedValue / extraction.targetValue) * 100),
            )}%`,
          }}
        />
      </i>
      {extraction.cargoLabel !== undefined && (
        <small>
          TOWING {extraction.cargoLabel.toUpperCase()} · {extraction.cargoMass ?? 0} MASS
        </small>
      )}
    </section>
  );
}

function Meter({ label, value, warning }: { label: string; value: number; warning?: boolean }) {
  return (
    <div className={styles.toolMeter} data-warning={warning === true}>
      <span>{label}</span>
      <i>
        <b style={{ width: `${String(Math.min(100, Math.max(0, value)))}%` }} />
      </i>
      <output>{Math.round(value)}</output>
    </div>
  );
}

export function ToolHud({ tool, onSelect }: { tool: ToolState; onSelect: (tool: ToolId) => void }) {
  return (
    <section className={styles.toolHud} data-valid-target={tool.validTarget === true}>
      <span className={styles.toolLabel}>DRONE TOOL ARRAY</span>
      <div className={styles.toolWheel}>
        {(Object.keys(TOOL_META) as ToolId[]).map((id) => {
          const meta = TOOL_META[id];
          return (
            <button
              aria-label={`Select ${meta.label}`}
              aria-pressed={tool.selected === id}
              disabled={tool.disabled === true}
              key={id}
              onClick={() => {
                onSelect(id);
              }}
            >
              <kbd>{meta.number}</kbd>
              <Icon name={meta.icon} />
              <span>{meta.label}</span>
            </button>
          );
        })}
      </div>
      <div className={styles.meters}>
        <Meter label="ENERGY" value={tool.energy} />
        <Meter label="HEAT" value={tool.heat} warning={tool.heat >= 75} />
        {tool.grappleTension !== undefined && (
          <Meter label="TENSION" value={tool.grappleTension} warning={tool.grappleTension >= 80} />
        )}
      </div>
      {tool.validTarget !== undefined && (
        <div className={styles.targetStatus}>
          <i /> {tool.validTarget ? 'VALID ATTACHMENT' : 'NO AUTHORIZED SEAM'}
        </div>
      )}
    </section>
  );
}

export function ContextActionBar({
  action,
  onAction,
}: {
  action?: ContextActionState | undefined;
  onAction: () => void;
}) {
  if (action === undefined) return null;
  return (
    <button
      className={styles.contextBar}
      data-danger={action.danger === true}
      disabled={!action.enabled}
      onClick={onAction}
    >
      <kbd>E</kbd>
      <span>
        <strong>{action.label}</strong>
        <small>{action.hint}</small>
      </span>
      <Icon name="arrow" />
      {action.progress !== undefined && (
        <i style={{ width: `${String(action.progress * 100)}%` }} />
      )}
    </button>
  );
}

export function EventTicker({ events }: { events: readonly EventMessage[] }) {
  const visible = events.slice(-2);
  return (
    <div aria-atomic="true" aria-live="polite" className={styles.eventTicker}>
      {visible.map((event) => (
        <p data-tone={event.tone ?? 'info'} key={event.id}>
          <strong>{event.title}</strong>
          <span>{event.detail}</span>
        </p>
      ))}
    </div>
  );
}

export function LastWreckScreen({ state, actions, gameCanvas, className }: LastWreckScreenProps) {
  const settings = useSettingsStore((store) => store.settings);
  const [pauseOpen, setPauseOpen] = useState(state.phase === 'paused');
  const [contractSheet, setContractSheet] = useState(false);
  const [mapPausedByUi, setMapPausedByUi] = useState(false);
  const shellStyle = useMemo(
    () =>
      ({
        '--ui-text-scale': settings.textScale / 100,
        '--wreck-art': `url("${ASSET_MANIFEST.wreckBackdrop.src}")`,
        '--wreck-fallback': ASSET_MANIFEST.wreckBackdrop.fallback,
      }) as CSSProperties,
    [settings.textScale],
  );

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.highContrast = String(settings.highContrast);
    root.dataset.reducedMotion = String(settings.reducedMotion);
    root.dataset.oneHanded = String(settings.oneHandedMode);
  }, [settings.highContrast, settings.oneHandedMode, settings.reducedMotion]);

  const togglePause = (paused: boolean) => {
    setPauseOpen(paused);
    actions.pause(paused);
  };

  const openMap = () => {
    const mobileLayout = window.matchMedia(
      '(max-width: 700px), (pointer: coarse) and (max-width: 1024px)',
    ).matches;
    actions.setMapVisible(true);
    if (mobileLayout) {
      setMapPausedByUi(true);
      actions.pause(true);
    }
  };

  const closeMap = () => {
    actions.setMapVisible(false);
    if (mapPausedByUi) {
      setMapPausedByUi(false);
      actions.pause(false);
    }
  };

  return (
    <main
      className={clsx(styles.screen, className)}
      data-emergency={state.emergencySeconds !== undefined}
      style={shellStyle}
    >
      <div className={styles.gameStage}>{gameCanvas}</div>
      <div aria-hidden="true" className={styles.vignette} />
      <div aria-hidden="true" className={styles.scanLines} />

      <div className={styles.topLeft}>
        <ObjectiveCard objective={state.objective} />
      </div>
      <div className={styles.topCenter}>
        <InstabilityGauge
          elapsedSeconds={state.elapsedSeconds}
          emergencySeconds={state.emergencySeconds}
          value={state.instability}
        />
      </div>
      <div className={styles.systemButtons}>
        <button aria-label="Open scanner map" className={styles.iconButton} onClick={openMap}>
          <Icon name="map" />
        </button>
        <button
          aria-label="Pause and settings"
          className={styles.iconButton}
          onClick={() => {
            togglePause(true);
          }}
        >
          <Icon name="pause" />
        </button>
      </div>

      <div className={styles.bottomLeft}>
        <ExtractionHud extraction={state.extraction} />
      </div>
      <div className={styles.bottomRight}>
        <ToolHud onSelect={actions.selectTool} tool={state.tool} />
      </div>
      <div className={styles.bottomCenter}>
        <ContextActionBar action={state.contextAction} onAction={actions.contextAction} />
      </div>
      <EventTicker events={state.events} />

      <button
        className={styles.mobileContractTab}
        onClick={() => {
          setContractSheet(true);
          actions.pause(true);
        }}
      >
        <Icon name="target" /> Contract
      </button>

      <ScannerMapOverlay map={state.map} onClose={closeMap} />
      <MobileControls actions={actions} tool={state.tool} />

      {contractSheet && (
        <div className={styles.mobileSheetScrim}>
          <section className={styles.mobileSheet}>
            <header>
              <span>ACTIVE CONTRACT</span>
              <button
                aria-label="Close contract"
                className={styles.iconButton}
                onClick={() => {
                  setContractSheet(false);
                  actions.pause(false);
                }}
              >
                <Icon name="close" />
              </button>
            </header>
            <ObjectiveCard objective={state.objective} />
            <p>
              Recover the navigation core in usable condition. Banked optional salvage is retained
              even if the contract fails.
            </p>
          </section>
        </div>
      )}

      <PauseAndSettings
        onClose={() => {
          togglePause(false);
        }}
        onRestart={actions.restart}
        open={pauseOpen || state.phase === 'paused'}
      />
      {state.result !== undefined && (
        <RunResults
          onContinue={actions.continueAfterResult}
          onRestart={actions.restart}
          result={state.result}
        />
      )}
    </main>
  );
}
